import { Clickable, OnEscapeListener } from '@carvana/merch-components';
import cx from 'classnames';
import dynamic from 'next/dynamic';
import React, { useCallback, useRef } from 'react';
import FocusLock from 'react-focus-lock';
import { modalZIndex } from '~/constants/zIndex';
import { useIsMobile } from '~/contexts/DeviceContext';
import useClientSide from '~/hooks/useClientSide';
import {
  CloseIcon,
  Content,
  DesktopHeaderContainer,
  MobileHeaderContainer,
  ModalHeader,
  Wrapper
} from './Modal.styles';

const Portal = dynamic(
  () => import(/* webpackChunkName: "Portal" */ '@/Portal'),
  { ssr: false }
);

type OwnProps = React.PropsWithChildren<{
  className?: string;
  headerClassName?: string;
  disableFocusLock?: boolean;
  headerText?: React.ReactNode;
  id?: string;
  onToggle?: (event: React.MouseEvent) => void;
  open: boolean;
  showHeader?: boolean;
  whiteBg?: boolean;
}>;

const dummyFn = () => {};

export default function Modal({
  children,
  className = '',
  headerClassName = '',
  disableFocusLock,
  headerText = <>&nbsp;</>,
  id = 'modal',
  onToggle = dummyFn,
  open,
  showHeader = true,
  whiteBg = false
}: OwnProps) {
  const isMobile = useIsMobile();
  const isClientSide = useClientSide();
  const wrapperRef = useRef<HTMLElement>(null);

  const Header = useCallback(
    () => (
      <>
        <ModalHeader>{headerText}</ModalHeader>
        <Clickable
          element={CloseIcon}
          onClick={onToggle}
          data-analytics-id={null}
          data-qa="close-modal"
        />
      </>
    ),
    [headerText, onToggle]
  );

  return isClientSide ? (
    <Portal open={open} id={id}>
      <FocusLock autoFocus={false} disabled={disableFocusLock} group="modal">
        <OnEscapeListener onEscape={onToggle}>
          <Wrapper
            className={showHeader ? 'pt-44 ' : ''}
            onClick={(e: React.MouseEvent) => {
              if (e.target === wrapperRef.current) onToggle(e);
            }}
            data-analytics-id={null}
            ref={wrapperRef}
            style={{ zIndex: modalZIndex }}
          >
            {isMobile && showHeader && (
              <MobileHeaderContainer
                data-qa="header-container"
                style={{ zIndex: modalZIndex + 1 }}
                className={headerClassName}
              >
                <Header />
              </MobileHeaderContainer>
            )}
            <Content
              className={cx(className, whiteBg ? 'bg-white' : 'bg-grey-1')}
              data-cv-test="modal-content"
              data-qa="content"
            >
              {!isMobile && showHeader && (
                <DesktopHeaderContainer
                  data-qa="header-container"
                  style={{ zIndex: modalZIndex + 1 }}
                  className={headerClassName}
                >
                  <Header />
                </DesktopHeaderContainer>
              )}
              <div className="h-full overflow-auto m:h-auto">{children}</div>
            </Content>
          </Wrapper>
        </OnEscapeListener>
      </FocusLock>
    </Portal>
  ) : null;
}
